import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import WorkflowProgressIndicator from '../../components/ui/WorkflowProgressIndicator';
import AIAssistantChat from '../../components/ai/AIAssistantChat';
import ProductAnalysisPanel from '../../components/ai/ProductAnalysisPanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useOpenAI } from '../../hooks/useOpenAI';

const AIDashboard = () => {
  const [activeTab, setActiveTab] = useState('chat');
  const [recentAnalyses, setRecentAnalyses] = useState([]);
  const [quickActions, setQuickActions] = useState([]);

  const {
    supplierAnalysis,
    loading,
    error
  } = useOpenAI();

  // Mock data for recent analyses
  const mockRecentAnalyses = [
    {
      id: 1,
      type: 'Product Matching',
      product: 'Dell Laptop XPS 13',
      confidence: 0.92,
      timestamp: new Date(Date.now() - 300000),
      status: 'completed'
    },
    {
      id: 2,
      type: 'Supplier Analysis',
      supplier: 'TechWorld Distributors',
      confidence: 0.87,
      timestamp: new Date(Date.now() - 600000),
      status: 'completed'
    },
    {
      id: 3,
      type: 'Price Optimization',
      product: 'Samsung Monitor 27"',
      confidence: 0.78,
      timestamp: new Date(Date.now() - 900000),
      status: 'completed'
    }
  ];

  useEffect(() => {
    // Simulate loading recent analyses
    const timer = setTimeout(() => {
      setRecentAnalyses(mockRecentAnalyses);
      setQuickActions([
        {
          id: 1,
          title: 'Analyze Uploaded Files',
          description: 'Run AI analysis on recently uploaded supplier data',
          icon: 'Upload',
          action: () => handleQuickAction('analyze-files')
        },
        {
          id: 2,
          title: 'Generate Product Descriptions',
          description: 'Create AI-powered descriptions for products missing them',
          icon: 'FileText',
          action: () => handleQuickAction('generate-descriptions')
        },
        {
          id: 3,
          title: 'Price Trend Analysis',
          description: 'Analyze pricing trends across suppliers',
          icon: 'TrendingUp',
          action: () => handleQuickAction('price-trends')
        },
        {
          id: 4,
          title: 'Supplier Risk Assessment',
          description: 'Evaluate supplier reliability and risks',
          icon: 'Shield',
          action: () => handleQuickAction('risk-assessment')
        }
      ]);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const handleQuickAction = (actionType) => {
    console.log('Quick action triggered:', actionType);
    // Implement specific actions based on actionType
    switch (actionType) {
      case 'analyze-files':
        // Navigate to file analysis or trigger analysis
        break;
      case 'generate-descriptions':
        // Start description generation process
        break;
      case 'price-trends':
        // Navigate to price analysis
        break;
      case 'risk-assessment':
        // Start supplier risk assessment
        break;
      default:
        break;
    }
  };

  const handleAnalysisComplete = (result) => {
    const newAnalysis = {
      id: Date.now(),
      type: 'Product Analysis',
      product: 'Custom Analysis',
      confidence: result?.matching?.confidence_score || 0.5,
      timestamp: new Date(),
      status: 'completed'
    };

    setRecentAnalyses(prev => [newAnalysis, ...prev?.slice(0, 4)]);
  };

  const tabs = [
    { id: 'chat', label: 'AI Assistant', icon: 'Bot' },
    { id: 'analysis', label: 'Product Analysis', icon: 'Search' },
    { id: 'insights', label: 'Insights & Reports', icon: 'BarChart3' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Brain" size={24} color="white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">AI Dashboard</h1>
                <p className="text-muted-foreground">
                  AI-powered insights for product matching and supply chain optimization
                </p>
              </div>
            </div>
            
            {/* Workflow Progress */}
            <WorkflowProgressIndicator 
              currentStep={5}
              completedSteps={[0, 1, 2, 3, 4]}
              className="mt-6"
            />
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Left Sidebar - Quick Actions & Recent */}
            <div className="lg:col-span-1 space-y-6">
              {/* AI Stats */}
              <div className="bg-card border border-border rounded-lg p-4">
                <h3 className="font-semibold text-foreground mb-4">AI Performance</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Analyses Today</span>
                    <span className="font-semibold text-foreground">24</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Avg. Confidence</span>
                    <span className="font-semibold text-foreground">89%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Processing Time</span>
                    <span className="font-semibold text-foreground">2.3s</span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-card border border-border rounded-lg p-4">
                <h3 className="font-semibold text-foreground mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  {quickActions?.map((action) => (
                    <Button
                      key={action?.id}
                      variant="ghost"
                      size="sm"
                      onClick={action?.action}
                      className="w-full justify-start h-auto p-3 text-left"
                    >
                      <div className="flex items-start space-x-3">
                        <Icon name={action?.icon} size={16} className="mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-sm">{action?.title}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {action?.description}
                          </div>
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>

              {/* Recent Analyses */}
              <div className="bg-card border border-border rounded-lg p-4">
                <h3 className="font-semibold text-foreground mb-4">Recent Analyses</h3>
                <div className="space-y-3">
                  {recentAnalyses?.slice(0, 5)?.map((analysis) => (
                    <div key={analysis?.id} className="border border-border rounded p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-foreground">
                          {analysis?.type}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {Math.round((analysis?.confidence || 0) * 100)}%
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mb-2">
                        {analysis?.product || analysis?.supplier}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {analysis?.timestamp?.toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-3">
              {/* Tab Navigation */}
              <div className="bg-card border border-border rounded-lg mb-6">
                <div className="flex border-b border-border">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === tab?.id
                          ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Icon name={tab?.icon} size={16} />
                      <span>{tab?.label}</span>
                    </button>
                  ))}
                </div>

                <div className="p-6">
                  {/* Tab Content */}
                  {activeTab === 'chat' && (
                    <div className="h-[600px]">
                      <AIAssistantChat 
                        className="h-full"
                        systemPrompt="You are an AI assistant specializing in product matching, price analysis, and supply chain optimization for an enterprise price matching platform. Help users with product analysis, supplier insights, pricing strategies, and data interpretation."
                      />
                    </div>
                  )}

                  {activeTab === 'analysis' && (
                    <ProductAnalysisPanel 
                      onAnalysisComplete={handleAnalysisComplete}
                    />
                  )}

                  {activeTab === 'insights' && (
                    <div className="space-y-6">
                      <div className="text-center py-8">
                        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                          <Icon name="BarChart3" size={24} color="currentColor" className="text-muted-foreground" />
                        </div>
                        <h4 className="text-lg font-medium text-foreground mb-2">AI Insights & Reports</h4>
                        <p className="text-sm text-muted-foreground mb-6">
                          Generate comprehensive reports and insights from your data using AI analysis
                        </p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                          <Button variant="outline" className="h-20 flex-col space-y-2">
                            <Icon name="TrendingUp" size={20} />
                            <span>Price Trend Report</span>
                          </Button>
                          <Button variant="outline" className="h-20 flex-col space-y-2">
                            <Icon name="Users" size={20} />
                            <span>Supplier Performance</span>
                          </Button>
                          <Button variant="outline" className="h-20 flex-col space-y-2">
                            <Icon name="Target" size={20} />
                            <span>Match Accuracy Report</span>
                          </Button>
                          <Button variant="outline" className="h-20 flex-col space-y-2">
                            <Icon name="AlertTriangle" size={20} />
                            <span>Risk Assessment</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AIDashboard;